import * as React from "react";
import {
  PageHeader,
  Alert,
  Table,
  Button,
  Space,
  Modal,
  Descriptions,
  Tag,
  Empty,
  Tooltip,
  Input,
  message as antdMessage,
} from "antd";
import {
  DeleteOutlined,
  CheckCircleOutlined,
  EnvironmentOutlined,
  WarningOutlined,
  ReloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { InventoryClient, ProductStockEntriesResponseDto } from "../utils/scanner-backend";
import { ApiConfig } from "../utils/AuthorizedApiBase";
import { getApiErrorMessage } from "../utils/apiError";
import { BarcodeScanResult } from "../hooks/useBarcodeScanner";
import { useKeyboardScanner } from "../hooks/useKeyboardScanner";
import { playAudioFeedback } from "../utils/audioFeedback";
import { useNotification } from "../hooks/useNotification";
import { useCharacterMap } from "../hooks/useCharacterMap";
import { useUserPosition } from "../hooks/useUserPosition";
import {
  parseProductBarcode,
  isPositionCode,
  extractPosition,
  formatBarcodeForDisplay,
} from "../utils/barcodeUtils";
import { CameraScanner } from "./shared/CameraScanner";
import { NumericKeyboard } from "./shared/NumericKeyboard";

// Initialize API client
const inventoryClient = new InventoryClient(new ApiConfig());

// LocalStorage key for session persistence
const STORAGE_KEY = "position-stocktaking-session";

interface ScannedItem {
  id: string; // unique identifier for React keys
  productCode: string;
  productName?: string;
  batchNumber: string;
  scannedQuantity: number;
  systemQuantity?: number;
  totalShelfQuantity?: number;
  accountingQuantity?: number;
  unit?: string;
  skuId?: number;
  hasError?: boolean; // Flag for items with missing critical data
  errorMessage?: string; // Description of the error
}

// Minimal item data for localStorage (API data will be reloaded)
interface StoredItem {
  productCode: string;
  batchNumber: string;
  scannedQuantity: number;
  skuId?: number;
}

interface StoredSession {
  scanPhase: ScanPhase;
  position: string;
  items: StoredItem[];
  note?: string; // missing in sessions saved before notes existed (S-532)
}

type ScanPhase = "WAITING_POSITION" | "SCANNING_ITEMS" | "READY_TO_SUBMIT";

const PositionStocktaking: React.FC = () => {
  // Core scanning state
  const [scanPhase, setScanPhase] = React.useState<ScanPhase>("WAITING_POSITION");
  const [position, setPosition] = React.useState<string>("");
  const [scannedItems, setScannedItems] = React.useState<ScannedItem[]>([]);
  const [note, setNote] = React.useState("");

  // UI state
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [cameraStatus, setCameraStatus] = React.useState<'idle' | 'initializing' | 'ready' | 'error'>('idle');
  const [isLoadingProduct, setIsLoadingProduct] = React.useState(false);

  // Notification system (floating messages, no layout shift)
  const notify = useNotification();

  // Character map for keyboard scanner
  const { isConfigured: isCharacterMapConfigured } = useCharacterMap();

  // User position for quick set
  const { userPosition, isAvailable: isUserPositionAvailable } = useUserPosition();

  // Debug state
  const [debugInfo, setDebugInfo] = React.useState<{
    timestamp: string;
    rawCode: string;
    translatedCode: string;
    codeLength: number;
    hasAt: boolean;
    endsWithAt: boolean;
    isPosition: boolean;
    charMapConfigured: boolean;
  } | null>(null);
  const [showDebug, setShowDebug] = React.useState(false);

  // Flag to prevent saving during initial load
  const [isInitialLoad, setIsInitialLoad] = React.useState(true);

  // Flag to track if user has performed any action (scan, edit, delete)
  // localStorage is saved only when this is true
  const [hasUserActivity, setHasUserActivity] = React.useState(false);

  // Combined feedback handler (visual + audio)
  const showCombinedFeedback = React.useCallback((msg: string, type: "success" | "error" | "info" | "warning") => {
    notify.notify(type, msg);
    if (type === "success" || type === "error") {
      playAudioFeedback(type);
    }
  }, [notify]);

  // Save current session to localStorage
  const saveToLocalStorage = React.useCallback(() => {
    try {
      // Don't save empty state - clear localStorage instead
      if (!position && scannedItems.length === 0) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }

      const session: StoredSession = {
        scanPhase,
        position,
        items: scannedItems.map(item => ({
          productCode: item.productCode,
          batchNumber: item.batchNumber,
          scannedQuantity: item.scannedQuantity,
          skuId: item.skuId,
        })),
        note,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    } catch (error) {
      console.error("Failed to save session to localStorage:", error);
    }
  }, [scanPhase, position, scannedItems, note]);

  // Clear localStorage
  const clearLocalStorage = React.useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      console.error("Failed to clear localStorage:", error);
    }
  }, []);

  // Helper to check if an item has errors
  const isItemProblematic = (item: ScannedItem): boolean => {
    return !item.skuId || !item.productName || item.hasError === true;
  };

  // Count problematic items
  const problematicItemsCount = React.useMemo(() => {
    return scannedItems.filter(isItemProblematic).length;
  }, [scannedItems]);

  // Load product details and stock entries from backend
  const loadProductData = React.useCallback(async (productCode: string): Promise<ProductStockEntriesResponseDto> => {
    try {
      const productData = await inventoryClient.getStockEntriesByCode(productCode);
      if (!productData || !productData.SkuId) {
        throw new Error(`Produkt s kódom "${productCode}" nebol nájdený v systéme.`);
      }
      return productData;
    } catch (error) {
      console.error("Failed to load product data:", error);
      // Re-throw with meaningful message
      throw error;
    }
  }, []);

  // Load products at a position
  const loadPositionProducts = React.useCallback(async (positionId: string) => {
    try {
      const products = await inventoryClient.getPositionProducts(positionId);
      // Convert to ScannedItem format with systemQuantity pre-filled
      const items: ScannedItem[] = products.map((p) => ({
        id: `${Date.now()}-${Math.random()}`,
        productCode: p.ProductCode,
        productName: p.ProductName,
        batchNumber: p.BatchNumber,
        scannedQuantity: p.Quantity ?? 0, // Start with system quantity (default to 0 if null)
        systemQuantity: p.Quantity ?? 0,
        totalShelfQuantity: p.AmountInWarehouse,
        accountingQuantity: p.AmountOnStockAccounting,
        unit: p.Unit,
        skuId: p.SkuId,
      }));
      setScannedItems(items);
    } catch (error) {
      console.error("Failed to load position products:", error);
      // Non-blocking - just log the error, allow user to continue
    }
  }, []);

  // Load saved session from localStorage on mount
  React.useEffect(() => {
    const loadFromLocalStorage = async () => {
      try {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (!saved) {
          setIsInitialLoad(false);
          return;
        }

        const session: StoredSession = JSON.parse(saved);

        // Restore position, scanPhase and note
        setPosition(session.position);
        setScanPhase(session.scanPhase);
        setNote(session.note ?? "");

        // Restore items with fresh product data from API
        if (session.items.length > 0) {
          const restoredItems: ScannedItem[] = [];

          for (const storedItem of session.items) {
            try {
              const productData = await loadProductData(storedItem.productCode);

              // Find system quantity for this position and batch
              const matchingEntry = productData.StockEntries?.find(
                (entry) => entry.PositionId === session.position && entry.BatchNumber === storedItem.batchNumber
              );

              restoredItems.push({
                id: `${Date.now()}-${Math.random()}`,
                productCode: storedItem.productCode,
                batchNumber: storedItem.batchNumber,
                scannedQuantity: storedItem.scannedQuantity,
                skuId: storedItem.skuId ?? productData.SkuId,
                productName: productData.ProductName,
                unit: productData.Unit,
                systemQuantity: matchingEntry?.Quantity ?? 0,
                totalShelfQuantity: productData.AmountInWarehouse,
                accountingQuantity: productData.AmountOnStockAccounting,
              });
            } catch (error) {
              console.error(`Failed to restore product ${storedItem.productCode}:`, error);
              // Skip items that fail to load - they may no longer exist in the system
            }
          }

          setScannedItems(restoredItems);
          // Mark as having user activity since we restored a session with items
          if (restoredItems.length > 0) {
            setHasUserActivity(true);
          }
        }
      } catch (error) {
        console.error("Failed to load session from localStorage:", error);
      } finally {
        setIsInitialLoad(false);
      }
    };

    loadFromLocalStorage();
  }, [loadProductData]);

  // Auto-save to localStorage when state changes (after initial load and only if user has activity)
  React.useEffect(() => {
    if (!isInitialLoad && hasUserActivity) {
      saveToLocalStorage();
    }
  }, [isInitialLoad, hasUserActivity, saveToLocalStorage]);

  // Handle scanned code processing
  const processScannedCode = React.useCallback(async (code: string, rawCode?: string) => {
    if (!code) return;

    // Update debug info
    setDebugInfo({
      timestamp: new Date().toLocaleTimeString(),
      rawCode: rawCode || code,
      translatedCode: code,
      codeLength: code.length,
      hasAt: code.includes('@'),
      endsWithAt: code.trim().endsWith('@'),
      isPosition: isPositionCode(code),
      charMapConfigured: isCharacterMapConfigured,
    });

    // Check for position marker (@)
    if (isPositionCode(code)) {
      const pos = extractPosition(code);
      if (!pos) {
        showCombinedFeedback("Neplatná pozícia!", "error");
        return;
      }

      // If waiting for position
      if (scanPhase === "WAITING_POSITION") {
        setPosition(pos);
        setScanPhase("SCANNING_ITEMS");
        await loadPositionProducts(pos);
        showCombinedFeedback(`Pozícia: ${pos} nastavená. Skenujte produkty.`, "success");
        return;
      }

      // If already scanning items - warn that position was already set
      if (scanPhase === "SCANNING_ITEMS" || scanPhase === "READY_TO_SUBMIT") {
        showCombinedFeedback("Pozícia už bola nastavená. Pre zmenu pozície zrušte inventúru a začnite odznova.", "error");
        return;
      }
    }

    // Parse product barcode
    if (scanPhase === "SCANNING_ITEMS" || scanPhase === "READY_TO_SUBMIT") {
      const parsed = parseProductBarcode(code);
      if (!parsed) {
        showCombinedFeedback("Neplatný formát kódu produktu!", "error");
        return;
      }

      // Check if this product + batch combination already exists
      const existingIndex = scannedItems.findIndex(
        (item) => item.productCode === parsed.productCode && item.batchNumber === parsed.batchNumber
      );

      if (existingIndex >= 0) {
        // Update existing item quantity
        const updatedItems = [...scannedItems];
        updatedItems[existingIndex].scannedQuantity += parsed.quantity;
        setScannedItems(updatedItems);
        setHasUserActivity(true);
        showCombinedFeedback(
          `Aktualizované: ${formatBarcodeForDisplay(parsed, updatedItems[existingIndex].unit)}, celkom ${updatedItems[existingIndex].scannedQuantity}`,
          "success"
        );
      } else {
        // Load product details and stock entries in one call
        setIsLoadingProduct(true);
        try {
          const productData = await loadProductData(parsed.productCode);

          // Verify skuId is present (defensive check)
          if (!productData.SkuId) {
            showCombinedFeedback(`Produkt "${parsed.productCode}" nemá platné SKU ID.`, "error");
            return;
          }

          // Find system quantity for this position and batch
          const matchingEntry = productData.StockEntries?.find(
            (entry) => entry.PositionId === position && entry.BatchNumber === parsed.batchNumber
          );

          // Add new item with loaded product details
          const newItem: ScannedItem = {
            id: `${Date.now()}-${Math.random()}`,
            productCode: parsed.productCode,
            productName: productData.ProductName,
            batchNumber: parsed.batchNumber,
            scannedQuantity: parsed.quantity,
            systemQuantity: matchingEntry?.Quantity ?? 0,
            totalShelfQuantity: productData.AmountInWarehouse,
            accountingQuantity: productData.AmountOnStockAccounting,
            unit: productData.Unit,
            skuId: productData.SkuId,
            hasError: false,
          };

          setScannedItems((prev) => [...prev, newItem]);
          setHasUserActivity(true);
          showCombinedFeedback(
            `Pridané: ${formatBarcodeForDisplay(parsed, productData.Unit)}`,
            "success"
          );
        } catch (error) {
          console.error("Failed to load product:", error);
          const errorMsg = getApiErrorMessage(error);
          showCombinedFeedback(`Nepodarilo sa načítať produkt: ${errorMsg}`, "error");
          return;
        } finally {
          setIsLoadingProduct(false);
        }
      }

      setScanPhase("SCANNING_ITEMS");
    } else {
      showCombinedFeedback("Najprv naskenujte pozíciu!", "error");
    }
  }, [scanPhase, scannedItems, showCombinedFeedback, loadProductData, loadPositionProducts, isCharacterMapConfigured]);

  // Camera barcode scanner
  const handleCameraScan = React.useCallback(async (result: BarcodeScanResult) => {
    await processScannedCode(result.rawValue);
  }, [processScannedCode]);

  // Keyboard scanner (HID scanner)
  useKeyboardScanner(processScannedCode);

  // Remove item from list
  const removeItem = (itemId: string) => {
    setScannedItems((prev) => prev.filter((item) => item.id !== itemId));
    setHasUserActivity(true);
  };

  // Update scanned quantity manually
  const updateScannedQuantity = (itemId: string, newQuantity: number) => {
    setScannedItems((prev) =>
      prev.map((item) =>
        item.id === itemId ? { ...item, scannedQuantity: newQuantity } : item
      )
    );
    setHasUserActivity(true);
  };

  const updateNote = (newNote: string) => {
    setNote(newNote);
    setHasUserActivity(true);
  };

  // Submit the stocktaking for current position and auto-reset for next
  const handleSubmit = async () => {
    if (!position) {
      antdMessage.error("Nastavte pozíciu!");
      return;
    }

    // Allow empty positions - confirm with user
    if (scannedItems.length === 0) {
      Modal.confirm({
        title: "Potvrdiť prázdnu pozíciu",
        content: (
          <div>
            <p>Pozícia <strong>{position}</strong> nemá žiadne naskenované produkty.</p>
            <p>Chcete potvrdiť, že táto pozícia je skutočne prázdna a neobsahuje žiadne produkty?</p>
          </div>
        ),
        okText: "Áno, potvrdiť prázdnu pozíciu",
        cancelText: "Nie, pokračovať v skenovaní",
        onOk: async () => {
          await submitStocktaking();
        },
      });
      return;
    }

    await submitStocktaking();
  };

  // Internal submit function
  const submitStocktaking = async () => {

    setIsSubmitting(true);

    try {
      // Validate that all items have valid skuIds
      const invalidItems = scannedItems.filter(item => !item.skuId);
      if (invalidItems.length > 0) {
        const invalidCodes = invalidItems.map(item => item.productCode).join(', ');
        antdMessage.error(`Niektoré produkty nemajú platné SKU ID: ${invalidCodes}. Odstráňte ich a skúste znova.`);
        return;
      }

      await inventoryClient.processPositionStockTaking({
        StoragePositionId: position,
        Items: scannedItems.map(item => ({
          StoragePositionId: position,
          SkuId: item.skuId!,
          SkuCode: item.productCode,
          BatchNumber: item.batchNumber,
          ExpectedQuantity: item.systemQuantity ?? 0,
          ActualQuantity: item.scannedQuantity,
        })),
        Note: note.trim() || null,
      });

      // Show success message
      const itemCount = scannedItems.length;
      const message = itemCount === 0
        ? `Pozícia ${position} označená ako prázdna`
        : `Pozícia ${position} uložená (${itemCount} ${itemCount === 1 ? 'produkt' : itemCount < 5 ? 'produkty' : 'produktov'})`;

      // Auto-reset for next position
      setScannedItems([]);
      setNote("");
      setPosition("");
      setScanPhase("WAITING_POSITION");
      setHasUserActivity(false);
      clearLocalStorage();
      showCombinedFeedback(message, "success");
    } catch (error) {
      console.error("Stocktaking submission failed:", error);
      const errorMsg = getApiErrorMessage(error);
      antdMessage.error(`Chyba pri ukladaní pozície ${position}: ${errorMsg}`);
      showCombinedFeedback(`Chyba: ${errorMsg}`, "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset current position only (not session stats)
  const handleReset = () => {
    Modal.confirm({
      title: "Zrušiť aktuálnu pozíciu?",
      content: `Naskenované položky pre pozíciu "${position}" budú odstránené. Session bude pokračovať.`,
      okText: "Áno, zrušiť",
      cancelText: "Nie",
      onOk: () => {
        setScannedItems([]);
        setNote("");
        setPosition("");
        setScanPhase("WAITING_POSITION");
        setHasUserActivity(false);
        clearLocalStorage();
        notify.destroy(); // Clear any active notifications
        showCombinedFeedback("Pozícia zrušená. Skenujte novú pozíciu.", "info");
      },
    });
  };

  // Set position to user's position
  const handleUseMyPosition = async () => {
    if (!userPosition) return;
    setPosition(userPosition);
    setScanPhase("SCANNING_ITEMS");
    await loadPositionProducts(userPosition);
    showCombinedFeedback(`Pozícia: ${userPosition} nastavená. Skenujte produkty.`, "success");
  };

  // Calculate differences
  const getDifference = (item: ScannedItem): number | undefined => {
    if (item.systemQuantity === undefined) return undefined;
    return item.scannedQuantity - item.systemQuantity;
  };

  const getInventoryMatchState = (item: ScannedItem): "matching" | "mismatch" | "missing" => {
    if (item.totalShelfQuantity === undefined || item.accountingQuantity === undefined) {
      return "missing";
    }

    return item.totalShelfQuantity === item.accountingQuantity ? "matching" : "mismatch";
  };

  const getInventoryMatchLabel = (item: ScannedItem): string => {
    const state = getInventoryMatchState(item);
    if (state === "matching") return "Sedí";
    if (state === "mismatch") return "Nesedí";
    return "Bez údajov";
  };

  // Table columns
  const columns = [
    {
      title: "Produkt",
      key: "product",
      render: (item: ScannedItem) => {
        const isProblematic = isItemProblematic(item);
        const errorDetails = [];
        const inventoryMatchState = getInventoryMatchState(item);
        if (!item.skuId) errorDetails.push("Chýba SKU ID");
        if (!item.productName) errorDetails.push("Chýba názov produktu");
        if (item.hasError && item.errorMessage) errorDetails.push(item.errorMessage);

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {isProblematic && (
              <Tooltip title={errorDetails.join(", ")}>
                <WarningOutlined style={{ color: '#ff4d4f', fontSize: 18 }} />
              </Tooltip>
            )}
            <div>
              <strong style={{ color: isProblematic ? '#ff4d4f' : undefined }}>
                {item.productCode}
              </strong>
              {item.productName ? (
                <>
                  <br />
                  <span style={{ color: "#666" }}>{item.productName}</span>
                </>
              ) : (
                <>
                  <br />
                  <span style={{ color: "#ff4d4f", fontSize: 12 }}>Názov sa nepodarilo načítať</span>
                </>
              )}
              <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
                <Tag color={inventoryMatchState === "matching" ? "green" : inventoryMatchState === "mismatch" ? "red" : "default"}>
                  {getInventoryMatchLabel(item)}
                </Tag>
                <Tag color="blue">Police: {item.totalShelfQuantity ?? 0} {item.unit || ""}</Tag>
                <Tag color="gold">Pohoda: {item.accountingQuantity ?? 0} {item.unit || ""}</Tag>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Šarža",
      dataIndex: "batchNumber",
      key: "batchNumber",
      render: (text: string) => text || "-",
    },
    {
      title: "Naskenované",
      key: "scannedQuantity",
      render: (item: ScannedItem) => (
        <NumericKeyboard
          title="Zadajte množstvo"
          unit={item.unit}
          value={item.scannedQuantity}
          onChange={(value) => updateScannedQuantity(item.id, value)}
          onEnter={() => {
            // Close popover
          }}
        >
          <Button style={{ width: "100%", textAlign: "left" }}>
            {item.scannedQuantity} {item.unit}
          </Button>
        </NumericKeyboard>
      ),
    },
    {
      title: "V systéme",
      key: "systemQuantity",
      render: (item: ScannedItem) =>
        item.systemQuantity !== undefined
          ? `${item.systemQuantity} ${item.unit || ""}`
          : "-",
    },
    {
      title: "Rozdiel",
      key: "difference",
      render: (item: ScannedItem) => {
        const diff = getDifference(item);
        if (diff === undefined) return "-";

        const color = diff === 0 ? "green" : diff > 0 ? "blue" : "red";
        const prefix = diff > 0 ? "+" : "";

        return (
          <Tag color={color}>
            {prefix}{diff} {item.unit || ""}
          </Tag>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 80,
      render: (item: ScannedItem) => (
        <Button
          danger
          shape="circle"
          icon={<DeleteOutlined />}
          onClick={() => removeItem(item.id)}
          disabled={isSubmitting}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: "0 0 80px 0", paddingBottom: cameraStatus !== 'idle' ? 'calc(25vh + 140px)' : '140px' }}>
      <PageHeader
        className="site-page-header"
        onBack={() => window.history.back()}
        title="Inventúra pozície"
      />

      {/* Instructions */}
      <Alert
        message="Ako používať inventúru pozície"
        description={
          <ol style={{ marginBottom: 0, paddingLeft: 20 }}>
            <li>Naskenujte <strong>QR kód pozície</strong></li>
            <li>Naskenujte <strong>všetky produkty</strong> na tejto pozícii</li>
            <li>Upravte množstvá ak je potrebné</li>
            <li>Kliknite <strong>Uložiť pozíciu</strong> - pozícia sa uloží</li>
          </ol>
        }
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* Status */}
      <Descriptions column={{xs: 1, sm: 2}} bordered size="small" style={{ marginBottom: 16 }}>
          <Descriptions.Item label="Krok">
            {scanPhase === "WAITING_POSITION" && "1. Čakám na pozíciu"}
            {scanPhase === "SCANNING_ITEMS" && "2. Skenujem produkty"}
            {scanPhase === "READY_TO_SUBMIT" && "3. Pripravené na odoslanie"}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            {isSubmitting ? (
              <Tag color="blue">Odosielam...</Tag>
            ) : scanPhase === "SCANNING_ITEMS" && scannedItems.length > 0 ? (
              <Tag color="green">Pripravené</Tag>
            ) : (
              <Tag color="orange">Skenovanie</Tag>
            )}
          </Descriptions.Item>
          {/* <Descriptions.Item label="Mapovanie znakov" span={2}>
            {isCharacterMapConfigured ? (
              <Tooltip title={`Source: ${charMapSource.substring(0, 20)}... Target: ${charMapTarget.substring(0, 20)}...`}>
                <Tag color="green" icon={<CheckCircleOutlined />}>Nakonfigurované</Tag>
              </Tooltip>
            ) : (
              <Tag color="red" icon={<WarningOutlined />}>Nenakonfigurované</Tag>
            )}
          </Descriptions.Item> */}
          <Descriptions.Item label={<><EnvironmentOutlined /> Pozícia</>} span={2}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              {position ? (
                <Tag color="blue" style={{ fontSize: 16, padding: "4px 12px" }}>
                  {position}
                </Tag>
              ) : (
                <span style={{ color: "#999" }}>Naskenujte pozíciu</span>
              )}
              {isUserPositionAvailable && scanPhase === "WAITING_POSITION" && (
                <Button
                  size="small"
                  icon={<EnvironmentOutlined />}
                  onClick={handleUseMyPosition}
                  type="primary"
                >
                  Nastaviť {userPosition}
                </Button>
              )}
            </div>
          </Descriptions.Item>
          <Descriptions.Item label="Položky na pozícii">
            {scannedItems.length}
          </Descriptions.Item>
        </Descriptions>

      {/* Debug Panel */}
      {showDebug && debugInfo && (
        <Alert
          message={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <strong>Debug Info</strong>
              <Button size="small" onClick={() => setShowDebug(false)}>Skryť</Button>
            </div>
          }
          description={
            <div style={{ fontFamily: 'monospace', fontSize: 12 }}>
              <div><strong>Čas:</strong> {debugInfo.timestamp}</div>
              <div><strong>Raw kód:</strong> "{debugInfo.rawCode}"</div>
              <div><strong>Preložený kód:</strong> "{debugInfo.translatedCode}"</div>
              <div><strong>Dĺžka:</strong> {debugInfo.codeLength}</div>
              <div><strong>Obsahuje @:</strong> {debugInfo.hasAt ? '✓ Áno' : '✗ Nie'}</div>
              <div><strong>Končí na @:</strong> {debugInfo.endsWithAt ? '✓ Áno' : '✗ Nie'}</div>
              <div><strong>Je pozícia:</strong> {debugInfo.isPosition ? '✓ Áno' : '✗ Nie'}</div>
              <div><strong>Char map:</strong> {debugInfo.charMapConfigured ? '✓ Nakonfigurovaný' : '✗ Nenakonfigurovaný'}</div>
              {debugInfo.rawCode !== debugInfo.translatedCode && (
                <div style={{ marginTop: 8, color: '#1890ff' }}>
                  <strong>⚠️ Preklad aplikovaný!</strong>
                </div>
              )}
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}
      {!showDebug && (
        <Button
          size="small"
          onClick={() => setShowDebug(true)}
          style={{ marginBottom: 16 }}
        >
          Zobraziť Debug Info
        </Button>
      )}

      {/* Loading indicator */}
      {isLoadingProduct && (
        <Alert
          message={
            <span>
              <LoadingOutlined style={{ marginRight: 8 }} />
              Načítavam údaje o produkte...
            </span>
          }
          type="info"
          showIcon={false}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* Warning for problematic items */}
      {problematicItemsCount > 0 && (
        <Alert
          message="Pozor: Niektoré položky majú chýbajúce údaje"
          description={
            <div>
              <p>
                {problematicItemsCount} {problematicItemsCount === 1 ? 'položka má' : problematicItemsCount < 5 ? 'položky majú' : 'položiek má'} neúplné údaje (označené <WarningOutlined style={{ color: '#ff4d4f' }} />).
              </p>
              <p style={{ marginBottom: 0 }}>
                Tieto položky nemôžu byť uložené. Odstráňte ich alebo obnovte stránku a skúste znova.
              </p>
            </div>
          }
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
        />
      )}

      <Table
        dataSource={scannedItems}
        columns={columns}
        rowKey="id"
        pagination={false}
        scroll={{ x: true }}
        rowClassName={(item: ScannedItem) => {
          if (isItemProblematic(item)) return "problematic-row";

          const inventoryMatchState = getInventoryMatchState(item);
          if (inventoryMatchState === "matching") return "inventory-match-row";
          if (inventoryMatchState === "mismatch") return "inventory-mismatch-row";
          return "inventory-missing-row";
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="Pozícia je prázdna"
            />
          ),
        }}
      />

      {position && (
        <div style={{ marginTop: 16 }}>
          <label htmlFor="position-stocktaking-note" style={{ fontWeight: 600 }}>
            Poznámka
          </label>
          <Input.TextArea
            id="position-stocktaking-note"
            value={note}
            onChange={(e) => updateNote(e.target.value)}
            placeholder="Voliteľné, uloží sa s inventúrou pozície"
            autoSize={{ minRows: 2, maxRows: 6 }}
            enterKeyHint="done"
            // Enter finishes the note instead of starting a new line: a barcode scanned while the note
            // still has focus would otherwise vanish into it, along with every scan after it.
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                e.currentTarget.blur();
              }
            }}
            disabled={isSubmitting}
            style={{ marginTop: 4 }}
          />
        </div>
      )}

      <style>{`
        .problematic-row {
          background-color: #fff1f0 !important;
        }
        .problematic-row:hover > td {
          background-color: #ffccc7 !important;
        }
        .inventory-match-row > td {
          background-color: #f6ffed !important;
        }
        .inventory-match-row:hover > td {
          background-color: #d9f7be !important;
        }
        .inventory-mismatch-row > td {
          background-color: #fff2f0 !important;
        }
        .inventory-mismatch-row:hover > td {
          background-color: #ffccc7 !important;
        }
        .inventory-missing-row > td {
          background-color: #fafafa !important;
        }
        .inventory-missing-row:hover > td {
          background-color: #f0f0f0 !important;
        }
        .position-stocktaking-button-row .ant-space-item:last-child {
          flex-grow: 1;
        }
      `}</style>

      {/* Action Buttons - Fixed at bottom */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          borderTop: "1px solid #f0f0f0",
          padding: "8px",
          zIndex: 100,
          boxSizing: "border-box",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div style={{ width: "100%", maxWidth: "600px" }}>
          {problematicItemsCount > 0 && (
            <div style={{ textAlign: 'center', color: '#ff4d4f', fontSize: 12, marginBottom: 8 }}>
              <WarningOutlined style={{ marginRight: 4 }} />
              Odstráňte položky s chybami pred uložením
            </div>
          )}

          {/* Single row with all buttons */}
          <Space className="position-stocktaking-button-row" style={{ width: "100%", display: "flex" }}>
            {/* Camera Scanner - always mounted */}
            <CameraScanner
              onScan={handleCameraScan}
              onCameraStatusChange={setCameraStatus}
              buttonSize="large"
            />

            {/* Reset and Submit buttons - hidden when camera is active */}
            {cameraStatus === 'idle' && (
              <>
                {/* Reset Current Position Button */}
                <Button
                  size="large"
                  icon={<ReloadOutlined />}
                  onClick={handleReset}
                  disabled={isSubmitting || !position}
                  style={{ height: 48 }}
                />

                {/* Submit Button */}
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={!position || problematicItemsCount > 0}
                  block
                  style={{ height: 48 }}
                >
                  {isSubmitting ? "Ukladám..." : "Uložiť pozíciu a pokračovať"}
                </Button>
              </>
            )}
          </Space>
        </div>
      </div>
    </div>
  );
};

export default PositionStocktaking;
